import { MotionSens, MotionThresholds } from "@/enums/motion";
import { PROCESS_INTERVAL, ONE_SECOND, SCALE_FACTOR } from "./constants";
import DetectionZones from "./DetectionZones";
import { getMotionOverlayEnabled } from "./debugSettings";

type DiffMotionDetectorConfig = {
  sensitivity?: MotionDetectionSensitivity;
  onChange?: (isMotion: boolean) => void;
};

export default class DiffMotionDetector {
  state: "idle" | "running" = "idle";
  isMotion = false;

  private sensitivity: MotionDetectionSensitivity;
  private onChange?: (isMotion: boolean) => void;
  private detectionZones = new DetectionZones();
  private motionFrames = 0;
  private motionStartDate = 0;
  private canProcess = true;
  private clearExistingMats = () => {};
  private timeoutId: NodeJS.Timeout | null = null;

  constructor({ sensitivity = "MEDIUM", onChange }: DiffMotionDetectorConfig) {
    this.sensitivity = sensitivity;
    this.onChange = onChange;
  }

  setDetectionZone = (detectionZone: MotionDetectionZone | null) => {
    this.detectionZones.setDetectionZone(detectionZone);
    if (this.state === "running") this.detectionZones.updateMatrices();
  };

  setSensitivity = (sensitivity: MotionDetectionSensitivity) => {
    this.sensitivity = sensitivity;
  };

  start = async (video: HTMLVideoElement) => {
    if (this.state === "running") return;
    this.state = "running";
    log.info("About to start processing video for motion detection");

    const debugBox = getMotionOverlayEnabled() ? this.createDebugBox() : null;
    this.canProcess = true;
    const cv = await window.cv;

    video.height = video.offsetHeight;
    video.width = video.offsetWidth;
    const cap = new cv.VideoCapture(video);

    const frame = new cv.Mat(video.height, video.width, cv.CV_8UC4);
    const grayFrame = new cv.Mat();
    const refFrame = new cv.Mat();
    const diffFrame = new cv.Mat();
    const threshFrame = new cv.Mat();
    this.detectionZones.createMatrices(cv, video.height, video.width);
    this.clearExistingMats = () => {
      frame.delete();
      grayFrame.delete();
      refFrame.delete();
      diffFrame.delete();
      threshFrame.delete();
      this.detectionZones.deleteMatrices();
      debugBox?.remove();
      this.clearExistingMats = () => {};
    };

    let averageMotion = 0;

    const processVideo = () => {
      try {
        if (!this.canProcess) {
          this.clearExistingMats();
          return;
        }

        const frameStartMs = performance.now();
        cap.read(frame);
        cv.resize(frame, grayFrame, new cv.Size(0, 0), SCALE_FACTOR, SCALE_FACTOR);
        cv.cvtColor(grayFrame, grayFrame, cv.COLOR_RGBA2GRAY);

        const nonZeroElements = cv.countNonZero(grayFrame);
        const isBrightEnough = this.checkVideoBrightness(grayFrame, nonZeroElements);
        if (!isBrightEnough) {
          this.timeoutId = setTimeout(processVideo, PROCESS_INTERVAL);
          return;
        }

        if (!refFrame.rows || !refFrame.cols) grayFrame.copyTo(refFrame);

        cv.absdiff(grayFrame, refFrame, diffFrame);
        cv.threshold(diffFrame, threshFrame, MotionSens[this.sensitivity].diffThreshold, 255, cv.THRESH_BINARY);

        this.detectionZones.bitwiseAnd(threshFrame);

        const newAverageMotion = cv.countNonZero(this.detectionZones.diffFrame || threshFrame);
        const elements = this.detectionZones.elements || grayFrame.rows * grayFrame.cols;

        averageMotion = 0.8 * averageMotion + 0.2 * newAverageMotion;
        const averageMotionElements = averageMotion / elements;

        const isAboveThreshold = averageMotionElements > MotionThresholds[this.sensitivity];

        if (isAboveThreshold) {
          this.onMotionFrame();
        } else {
          this.onRestFrame();
        }
        if (debugBox) {
          debugBox.updateStats(performance.now() - frameStartMs);
          cv.imshow("motion-debug-container", this.detectionZones.diffFrame || threshFrame);
        }

        grayFrame.copyTo(refFrame);
      } catch (err) {
        log.err("Failed 'processVideo'", err);
        this.forceRest();
      }

      this.timeoutId = setTimeout(processVideo, PROCESS_INTERVAL);
    };
    processVideo();
  };

  stop = () => {
    log.info("Stopping video processing for motion");
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.state = "idle";
    this.canProcess = false;
    this.isMotion = false;
    this.motionFrames = 0;
    this.motionStartDate = 0;
    this.clearExistingMats();
  };

  forceRest = () => {
    this.motionFrames = 0;
    this.emitIfChanged(false);
  };

  private onMotionFrame = () => {
    if (this.motionFrames < 2) {
      this.motionFrames += 1;
      return;
    }
    this.motionStartDate = Date.now();
    this.emitIfChanged(true);
  };

  private onRestFrame = () => {
    if (this.motionStartDate + ONE_SECOND > Date.now()) {
      return;
    }
    this.motionFrames = 0;
    this.emitIfChanged(false);
  };

  private emitIfChanged = (isMotion: boolean) => {
    if (isMotion === this.isMotion) return;
    this.isMotion = isMotion;
    this.onChange?.(isMotion);
  };

  private checkVideoBrightness = (grayFrame: any, nonZeroElements: any) => {
    const elements = grayFrame.rows * grayFrame.cols;
    const brightness = Math.round((nonZeroElements / elements) * 100);

    if (brightness <= 10) {
      log.motion("brightness too low");
      return false;
    } else {
      return true;
    }
  };

  private createDebugBox = () => {
    const cameraContainer = document.querySelector("#camera-video-container");
    const canvas = document.createElement("canvas");
    canvas.id = "motion-debug-container";
    canvas.style.position = "absolute";
    canvas.style.top = "0px";
    canvas.style.left = "0px";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.opacity = "0.2";
    canvas.style.transform = "scaleX(-1)";
    cameraContainer?.appendChild(canvas);

    const stats = document.createElement("div");
    stats.style.position = "absolute";
    stats.style.top = "8px";
    stats.style.left = "8px";
    stats.style.padding = "4px 8px";
    stats.style.background = "rgba(0, 0, 0, 0.6)";
    stats.style.color = "white";
    stats.style.font = "12px monospace";
    stats.style.pointerEvents = "none";
    cameraContainer?.appendChild(stats);

    return {
      updateStats: (processingMs: number) => {
        stats.textContent = `frame ${processingMs.toFixed(1)} ms`;
      },
      remove: () => {
        canvas.remove();
        stats.remove();
      }
    };
  };
}
